import { createSlice, createSelector } from "@reduxjs/toolkit";
import { FEATURES_CONFIG } from "../../featuresConfig";

const initialState = {
  allRounds: [], // All free spin rounds from API (could be 30+)
  currentFreeSpinRound: null, // Current round object for direct access
  symbolsRevealed: false, // Whether symbols are currently revealed (visible)
  freeSpinsActive: false, // Whether free spins are active
  currentFreeSpinRoundIndex: 0, // Current position in allRounds (0-29)
  totalFreeSpins: 15, // Total free spins (increases with retriggers)
  totalWin: 0, // Total accumulated win from all free spins played so far
  animatedTotalWin: 0, // Current animated total win value (persists across screen resizes)
  isPlayingRound: false, // Whether a round is currently being played (replaces local state)
  freeSpins: 0, // Total free spins (increases with retriggers)
  freeSpinAutoplayActive: false, // Whether free spin autoplay is active
  countedRoundIndices: [], // Track which round indices have had their wins added
  triggerRound: null, // The round that triggered free spins (for reference)
};

const freeSpinsSlice = createSlice({
  name: "freeSpins",
  initialState,
  reducers: {
    // Initialize free spins with rounds data (winType already calculated)
    initializeFreeSpins(state, action) {
      const { rounds, triggerRound } = action.payload; // Rounds already have winType calculated

      // Guard: Prevent duplicate initialization
      if (state.freeSpinsActive && state.allRounds.length === rounds.length) {
        return; // Already initialized with same data
      }

      state.allRounds = rounds;
      state.currentFreeSpinRoundIndex = 0;
      state.currentFreeSpinRound = rounds[0] || null;
      state.totalFreeSpins = 15;
      state.totalWin = 0;
      state.freeSpins = state.allRounds.length;
      state.countedRoundIndices = [];

      if (triggerRound) {
        state.triggerRound = triggerRound;
      }
    },

    setFreeSpinsActive(state, action) {
      state.freeSpinsActive = action.payload;
    },

    playNextRound(state) {
      if (state.currentFreeSpinRoundIndex >= state.allRounds.length - 1) return;
      state.currentFreeSpinRoundIndex += 1;
      state.currentFreeSpinRound =
        state.allRounds[state.currentFreeSpinRoundIndex] || null;
    },

    resetFreeSpins() {
      return initialState;
    },

    setSymbolsRevealed(state, action) {
      state.symbolsRevealed = action.payload;
    },

    setIsPlayingRound(state, action) {
      state.isPlayingRound = action.payload;
    },

    updateTotalWin(state) {
      const roundIndex = state.currentFreeSpinRoundIndex;
      const roundWin = state.currentFreeSpinRound?.win || 0;

      if (state.countedRoundIndices.includes(roundIndex)) return;

      state.countedRoundIndices.push(roundIndex);
      state.totalWin += roundWin;
    },

    addFreeSpins(state) {
      state.totalFreeSpins += FEATURES_CONFIG.FREE_SPINS.ROUNDS;
    },

    updateAnimatedTotalWin(state, action) {
      state.animatedTotalWin = action.payload;
    },

    setFreeSpinAutoplayActive(state, action) {
      state.freeSpinAutoplayActive = action.payload;
    },
  },
});

// ==============================
// 🔹 Derived Selectors
// ==============================

// ✅ Memoized selector for freeSpinsActive (prevents re-renders on unrelated updates)
export const selectFreeSpinsActive = (state) => state.freeSpins.freeSpinsActive;

// ✅ Memoized selector for isPlayingRound (same logic)
export const selectIsPlayingRound = createSelector(
  [(state) => state.freeSpins.isPlayingRound],
  (isPlayingRound) => isPlayingRound
);

// ✅ Check if last round
export const selectIsLastFreeSpinRound = createSelector(
  [
    (state) => state.freeSpins.currentFreeSpinRoundIndex,
    (state) => state.freeSpins.allRounds,
  ],
  (currentIndex, allRounds) => currentIndex >= allRounds.length - 1
);

// ✅ Check if complete
export const selectFreeSpinsComplete = createSelector(
  [selectIsLastFreeSpinRound, (state) => state.freeSpins.freeSpinRoundComplete],
  (isLastRound, freeSpinRoundComplete) => isLastRound && freeSpinRoundComplete
);

// ✅ Total win (raw, not guarded)
export const selectTotalFreeSpinWins = createSelector(
  [(state) => state.freeSpins.totalWin],
  (totalWin) => totalWin
);

// =======================================
// ✅ New: Total Win (safe for Outro dialog)
// =======================================
//
// This selector ensures that totalWin only updates when
// the Outro dialog is visible, preventing unnecessary
// component re-renders between rounds.
//
export const selectTotalWin = (state) => state.freeSpins.totalWin;

export const selectCurrentFreeSpinRound = (state) =>
  state.freeSpins.currentFreeSpinRound;

export const selectSymbolsRevealed = (state) => state.freeSpins.symbolsRevealed;
export const selectTotalWinForOutro = createSelector(
  [selectTotalWin, (state) => state.freeSpinAnimations.showFreeSpinOutro],
  (totalWin, showOutro) => (showOutro ? totalWin : null)
);

export const {
  initializeFreeSpins,
  playNextRound,
  resetFreeSpins,
  setFreeSpinsActive,
  setSymbolsRevealed,
  setIsPlayingRound,
  updateTotalWin,
  addFreeSpins,
  updateAnimatedTotalWin,
  completeFreeSpins,
  setFreeSpinAutoplayActive,
} = freeSpinsSlice.actions;

export default freeSpinsSlice.reducer;
